import {useMemo} from 'react';

import {LinePlotSettings} from '../../WorkspaceDrawer/Settings/types';
import {LINE_PLOT_TEMPLATE_FIELDS} from '../constants';
import {
  LinePlotTemplateSettings,
  WorkspaceTemplate,
} from '../workspaceTemplatesQueries';
import {LINE_PLOT_TEMPLATE_DISPLAY_FNS} from './linePlotTemplateDisplayFns';
import {useSmoothingTypeDisplayRow} from './smoothingDisplayValues';

export const useLinePlotTemplateSections = ({
  currTemplateLinePlotSettings,
  currWorkspaceLinePlotSettings,
}: {
  currTemplateLinePlotSettings: WorkspaceTemplate['linePlotSettings'];
  currWorkspaceLinePlotSettings?: LinePlotSettings;
}) => {
  // Call the smoothingType hook at the top level (before any conditionals or loops)
  const smoothingTypeRow = useSmoothingTypeDisplayRow({
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
  });

  return useMemo(() => {
    return [
      {
        title: 'Data',
        rows: LINE_PLOT_TEMPLATE_FIELDS.map(key => {
          // Use the hook result for smoothingType
          if (key === 'smoothingType') {
            return smoothingTypeRow;
          }

          // Use regular display functions for all other fields
          const displayFn = LINE_PLOT_TEMPLATE_DISPLAY_FNS[key];
          if (!displayFn) {
            throw new Error(
              `No display function found for field: ${key}. This should not happen.`
            );
          }
          return displayFn({
            currTemplateLinePlotSettings,
            currWorkspaceLinePlotSettings,
          });
        }),
      },
    ];
  }, [
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
    smoothingTypeRow,
  ]);
};

/**
 * These are values that should be copied over to the default spec when
 * a template is applied.
 */
export const getLinePlotTemplateValues = (
  workspaceTemplate: WorkspaceTemplate | undefined
): LinePlotSettings => {
  const templateLinePlotSettings = workspaceTemplate?.linePlotSettings;
  if (templateLinePlotSettings == null) {
    return {};
  }

  const linePlotSettings = LINE_PLOT_TEMPLATE_FIELDS.reduce<LinePlotSettings>(
    (acc, key: keyof LinePlotTemplateSettings) => {
      const templateValue = templateLinePlotSettings[key];

      // If a template value is undefined, there's no need to copy it over to spec
      // since that's the default already
      if (templateValue != null) {
        // This is safe to do since we've constrainted the types from the query already
        acc[key] = templateValue as any;
      }
      return acc;
    },
    {}
  );

  return linePlotSettings;
};
